import { Alert, Box, Button, Card, CardActions, CardContent, Chip, Divider, Link as MuiLink, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PanelSegment from '../../components/PanelSegment';
import PageTitle from '../../components/PageTitle';
import useViewportMode from '../../hooks/useViewportMode';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import { ProducerPayoutActivationAction, ProducerPayoutHistoryItem, ProducerPayoutStatus } from '../../models/producer';

const money = (value?: number) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format((value || 0) / 100);

const readinessLabel = (status?: ProducerPayoutStatus['VerificationState']) => {
    if (status === 'Ready')
        return 'Ready';
    if (status === 'PendingRequirements')
        return 'Pending requirements';
    if (status === 'Restricted')
        return 'Restricted';
    if (status === 'Unsupported')
        return 'Unsupported';
    return 'Unknown';
};

const readinessColor = (status?: ProducerPayoutStatus['VerificationState']): 'success' | 'warning' | 'error' | 'default' => {
    if (status === 'Ready')
        return 'success';
    if (status === 'PendingRequirements')
        return 'warning';
    if (status === 'Restricted')
        return 'error';
    return 'default';
};

const warningFromActions = (actions: ProducerPayoutActivationAction[] = []) => {
    if (actions.length === 0)
        return null;

    return <Alert severity="warning" sx={{ mb: 1.5 }}>
        <Typography component="div" variant="body2" sx={{ fontWeight: 600 }}>Required action pending</Typography>
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {actions.map((action, index) => <li key={`${action.Kind}-${index}`}>{action.Message}</li>)}
        </ul>
    </Alert>;
};

const payoutHistoryRows = (entries: ProducerPayoutHistoryItem[]) => <TableContainer component={Card} sx={{ mt: 1.5 }}>
    <Table size="small">
        <TableHead>
            <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Posted</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {entries.length === 0 && (
                <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>No payout history is available yet.</TableCell>
                </TableRow>
            )}
            {entries.map(item => <TableRow key={item.Id}>
                <TableCell>{item.PeriodLabel}</TableCell>
                <TableCell align="right">{money(item.AmountCents)}</TableCell>
                <TableCell>{item.Status}</TableCell>
                <TableCell>{item.ReferenceId || '-'}</TableCell>
                <TableCell>{item.PostedDate || '-'}</TableCell>
            </TableRow>)}
        </TableBody>
    </Table>
</TableContainer>;

const ProducerPayoutsPage = () => {
    const { mode, isDesktop } = useViewportMode();
    const { accessInfo, hasRole } = useAccessInfo();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<ProducerPayoutStatus>(null);
    const [launching, setLaunching] = useState(false);
    const isProducerAdmin = hasRole(Securables.producer_admin, true);
    const canReceivePayouts = status?.CanReceivePayouts ?? false;

    const openSetup = async () => {
        if (!accessInfo.selectedProducerCompanyId)
            return;

        try {
            setLaunching(true);
            const client = new ProducerPortalClient();
            const { Url } = canReceivePayouts
                ? await client.CreatePayoutDashboardSession(accessInfo.selectedProducerCompanyId)
                : await client.CreatePayoutOnboardingSession(accessInfo.selectedProducerCompanyId);
            window.open(Url, '_blank', 'noopener');
        } catch (error) {
            setError(error, 'Could not open payout setup right now.');
        } finally {
            setLaunching(false);
        }
    };

    const load = async () => {
        if (!isProducerAdmin || !accessInfo.selectedProducerCompanyId)
            return;

        try {
            setBusy(true);
            const payload = await new ProducerPortalClient().GetPayoutStatus(accessInfo.selectedProducerCompanyId);
            setStatus(payload);
        } catch (error) {
            setError(error, 'Could not load payout setup status.');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessInfo.selectedProducerCompanyId, isProducerAdmin]);

    if (!isProducerAdmin)
        return <Alert severity="warning">Only Producer Admin users can manage payout setup and payout history.</Alert>;

    const summary = status?.FinancialSummary;
    const canRun = canReceivePayouts;

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy || launching}>
            <PanelSegment
                titleContent={<PageTitle title="Producer payouts" />}
                toolbar={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button component={Link} to="/ProducerPortal" size="small" variant="outlined">Dashboard</Button>
                        <Button size="small" variant="text" onClick={() => void load()} sx={{ minHeight: isDesktop ? undefined : 44 }}>Refresh</Button>
                    </Stack>
                }
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Stripe payout account</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Payout setup is connected to Synergist’s hosted Stripe workflow. No bank account numbers are shown.
                    </Typography>

                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Stack spacing={1.5}>
                                <Stack direction={isDesktop ? 'row' : 'column'} spacing={1.25} alignItems="flex-start" justifyContent="space-between">
                                    <Stack spacing={0.5}>
                                        <Typography fontWeight={600}>Verification status</Typography>
                                        <Chip size="small" color={readinessColor(status?.VerificationState)} label={readinessLabel(status?.VerificationState)} />
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                        <Chip size="small" color={canRun ? 'success' : 'default'} label={`Payout account ready: ${canRun ? 'Yes' : 'No'}`} />
                                        <Chip size="small" color={status?.IsCapabilityReady ? 'success' : 'default'} label={`Capabilities: ${status?.IsCapabilityReady ? 'Ready' : 'Pending'}`} />
                                    </Stack>
                                </Stack>

                                <Typography variant="body2" color="text.secondary">
                                    {status?.IsVerificationComplete
                                        ? 'The producer payout identity has been verified by Stripe.'
                                        : 'Verification is incomplete. Complete the required setup steps before payouts can run.'}
                                </Typography>

                                {warningFromActions(status?.RequiredActions ?? [])}

                                <Typography variant="subtitle2" color="text.secondary">
                                    Statement status: {summary?.StatementStatus || 'unknown'} · Last payment status: {summary?.LastPaymentStatus || 'N/A'}
                                </Typography>

                                <CardActions sx={{ px: 0 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => void openSetup()}
                                        disabled={!accessInfo.selectedProducerCompanyId}
                                    >
                                        {canRun ? 'Open payout management' : 'Complete payout onboarding'}
                                    </Button>
                                    <MuiLink href="#" onClick={event => {
                                        event.preventDefault();
                                        window.open('/SubscriptionPage', '_self');
                                    }} color="secondary">Open help</MuiLink>
                                </CardActions>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Divider />

                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Payout financial visibility</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                        Projected earnings are available before payment completion. Amounts are not considered payable until Synergist receives the linked customer payment.
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 10, mb: 1.5 }}>
                        <Card variant="outlined" sx={{ minWidth: 180, flex: '1 1 220px' }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary">Monthly projected</Typography>
                                <Typography variant="h6">{money(summary?.MonthlyEarningsCents)}</Typography>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ minWidth: 180, flex: '1 1 220px' }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary">Projected carry-over</Typography>
                                <Typography variant="h6">{money(summary?.ProjectedEarningsCents)}</Typography>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ minWidth: 180, flex: '1 1 220px' }}>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary">Pending payout</Typography>
                                <Typography variant="h6">{money(summary?.PendingPayoutCents)}</Typography>
                            </CardContent>
                        </Card>
                    </Stack>

                    {summary?.LastPaymentStatus && summary?.LastPaymentStatus !== 'Paid' && summary?.LastPaymentIsPayable === false && <Alert severity="warning">This payout slice is not yet payable; wait for a successful customer settlement.</Alert>}

                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Payout history</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Immutable payout statements from the billing stream with adjustments and status history.
                    </Typography>
                    {payoutHistoryRows(status?.History || [])}
                </Box>
            </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default ProducerPayoutsPage;
